import { ref } from 'vue';
import { api, route } from '@ht/lib-plural-vue';
import useFlows from '@/composables/useFlows';
import useConfig from '@/composables/useConfig';
import useContent from '@/composables/useContent';

const loadingProjects = ref([]);

export default function useExternalProjects() {
  const { flows, setFlows } = useFlows();
  const { setConfig } = useConfig();
  const { setContentData } = useContent();

  function hasProject(token) {
    return flows.value[token] !== undefined;
  }

  async function loadProject(token) {
    loadingProjects.value.push(token);
    const exportData = await api.getExport('web', route.query.mode, token);
    loadingProjects.value = loadingProjects.value.filter((t) => t !== token);
    if (!exportData.error) {
      setConfig(token, exportData);
      setFlows(token, exportData);
      setContentData(token, exportData);
      return [token, exportData.config.externalProjects || []];
    }
    return [];
  }

  async function processExternalConfig(externalProjects) {
    if (!externalProjects || externalProjects.length === 0) {
      return;
    }

    const newProjects = await Promise.all(
      externalProjects
        .filter(({ token, preload }) => preload && !flows.value[token])
        .map(async ({ token }) => loadProject(token)),
    );

    // calculating recursive external projects by avoiding duplicates
    const nextConfig = newProjects
      .map(([token, extProjects]) => {
        if (!flows.value[token]) {
          return extProjects;
        }
        return [];
      })
      .flat()
      .reduce((acc, cur) => {
        if (acc[cur.token] && cur.preload) {
          acc[cur.token].preload = cur.preload;
        } else if (!acc[cur.token]) {
          acc[cur.token] = cur;
        }
        return acc;
      }, []);

    if (nextConfig.length) {
      processExternalConfig(nextConfig);
    }
  }

  return {
    loadingProjects,
    hasProject,
    processExternalConfig,
    loadProject,
  };
}
